/**
 * Blauw Cart Upsell — cart drawer quick-add handler.
 *
 * Uses event delegation so the listener survives section re-renders (morphdom).
 * Loaded once from header-actions.liquid; guarded against double-registration.
 *
 * Flow:
 *  1. User clicks a .blauw-upsell__chip button.
 *  2. POST /cart/add.js with the variant ID + request section HTML.
 *  3. Dispatch 'cart:update' with the returned sections payload.
 *  4. The Fabric theme's cart-items-component morphs the cart drawer,
 *     which re-renders this upsell with fresh cart state.
 *  5. The cart-icon component updates the bubble count via the same event.
 */

if (!window.__blauwUpsellRegistered) {
  window.__blauwUpsellRegistered = true;

  document.addEventListener('click', async (event) => {
    const btn = /** @type {HTMLElement|null} */ (event.target.closest('.blauw-upsell__chip'));
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const upsellEl = btn.closest('.blauw-upsell');
    const errorEl = /** @type {HTMLElement|null} */ (
      upsellEl?.querySelector('.blauw-upsell__error')
    );

    // Resolve the header section ID so the theme can morph the cart drawer.
    // data-section-id lives on both the .blauw-upsell wrapper (set by the snippet)
    // and on cart-items-component[data-drawer] as a reliable fallback.
    const sectionId =
      upsellEl?.dataset.sectionId ||
      document.querySelector('cart-items-component[data-drawer]')?.dataset.sectionId;

    // Clear previous error and enter loading state
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    btn.disabled = true;
    btn.classList.add('blauw-upsell__chip--loading');

    try {
      const body = {
        id: Number(variantId),
        quantity: 1,
      };
      // Request updated section HTML so the theme can morph without a round-trip.
      if (sectionId) body.sections = sectionId;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not add to cart.');
      }

      /**
       * Fire 'cart:update' — the Fabric theme's cart-items-component listens for this.
       * If event.detail.data.sections[sectionId] is present it calls morphSection();
       * otherwise it falls back to sectionRenderer.renderSection() (a fresh fetch).
       * The cart-icon component reads event.detail.data.itemCount to update the bubble.
       */
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            data: {
              sections: data.sections ?? {},
              itemCount: data.item_count ?? 0,
              source: 'blauw-upsell',
            },
          },
        })
      );
    } catch (err) {
      // Restore the button so the customer can try again
      btn.disabled = false;
      btn.classList.remove('blauw-upsell__chip--loading');

      if (errorEl) {
        errorEl.textContent =
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.';
        errorEl.hidden = false;
      }
    }
  });
}
